import { serializeComposerFileLink } from "@afar-research/shared/composerTrigger";

/**
 * Drag payload type carrying a serialized composer mention. Set on drags that
 * start in the workspace file tree so the composer can tell them apart from
 * OS file drags and plain text selections.
 */
export const COMPOSER_MENTION_DRAG_TYPE = "application/x-afar-research-composer-mention";

export function composerMentionFromTreePath(treePath: string): string | null {
  const relativePath = treePath.replace(/\/+$/, "");
  if (relativePath.length === 0) {
    return null;
  }
  return serializeComposerFileLink(relativePath);
}

export function dataTransferHasComposerMention(types: ReadonlyArray<string>): boolean {
  return types.includes(COMPOSER_MENTION_DRAG_TYPE);
}

/** An operating-system drag of files or folders, which the composer attaches. */
function dataTransferHasOsFiles(types: ReadonlyArray<string>): boolean {
  return types.includes("Files") && !dataTransferHasComposerMention(types);
}

/** Whether the composer claims this drag: a file-tree mention or OS files. */
export function dataTransferIsComposerDrop(types: ReadonlyArray<string>): boolean {
  return dataTransferHasComposerMention(types) || dataTransferHasOsFiles(types);
}

export interface ComposerMentionDragTransfer {
  readonly types: ReadonlyArray<string>;
  getData(format: string): string;
  dropEffect: string;
}

export interface ComposerMentionDragEvent {
  readonly dataTransfer: ComposerMentionDragTransfer;
  readonly nativeEvent: { stopPropagation(): void };
  preventDefault(): void;
  stopPropagation(): void;
}

/**
 * What a mention drop is allowed to do to the composer. Deliberately narrow:
 * there is no way to focus the editor from here. Focusing it synchronously
 * during the drop makes the not-yet-reconciled editor sync its stale empty
 * state back over the inserted mention; the insert path already focuses on
 * the next frame, after the editor has caught up.
 */
export interface ComposerDropHost<
  Transfer extends ComposerMentionDragTransfer = ComposerMentionDragTransfer,
> {
  insertMentionAtEnd(text: string): boolean;
  setDragActive(active: boolean): void;
  onInsertRejected(): void;
  /** Files dropped from the operating system become attachments. */
  attachDroppedFiles(transfer: Transfer): void;
}

export interface ComposerDropHandlers<
  Transfer extends ComposerMentionDragTransfer = ComposerMentionDragTransfer,
> {
  onDragEnter(event: ComposerMentionDragEvent & { readonly dataTransfer: Transfer }): void;
  onDragOver(event: ComposerMentionDragEvent & { readonly dataTransfer: Transfer }): void;
  onDrop(event: ComposerMentionDragEvent & { readonly dataTransfer: Transfer }): void;
}

/** Composer drop handling: file-tree drags become mentions, OS file drags attachments. */
export function makeComposerDropHandlers<
  Transfer extends ComposerMentionDragTransfer = ComposerMentionDragTransfer,
>(host: ComposerDropHost<Transfer>): ComposerDropHandlers<Transfer> {
  // Claim the event for the composer: React's stopPropagation only halts the
  // synthetic dispatch, so the native event must be stopped too or the
  // editor's own DOM listeners still process the drag.
  const claim = (event: ComposerMentionDragEvent): boolean => {
    if (!dataTransferIsComposerDrop(event.dataTransfer.types)) {
      return false;
    }
    event.preventDefault();
    event.stopPropagation();
    event.nativeEvent.stopPropagation();
    return true;
  };
  return {
    onDragEnter(event) {
      if (claim(event)) {
        host.setDragActive(true);
      }
    },
    onDragOver(event) {
      if (!claim(event)) {
        return;
      }
      // The tree constrains its drags to effectAllowed "move" and an OS drag
      // offers "copy"; naming any other effect makes the browser cancel the
      // drop without firing it.
      event.dataTransfer.dropEffect = dataTransferHasOsFiles(event.dataTransfer.types)
        ? "copy"
        : "move";
      host.setDragActive(true);
    },
    onDrop(event) {
      if (!claim(event)) {
        return;
      }
      host.setDragActive(false);
      if (dataTransferHasOsFiles(event.dataTransfer.types)) {
        host.attachDroppedFiles(event.dataTransfer);
        return;
      }
      const mention = event.dataTransfer.getData(COMPOSER_MENTION_DRAG_TYPE);
      if (mention.length === 0) {
        return;
      }
      if (!host.insertMentionAtEnd(`${mention} `)) {
        host.onInsertRejected();
      }
    },
  };
}
